/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    AxiosResponseHeaders,
    InternalAxiosRequestConfig,
    RawAxiosResponseHeaders,
} from 'axios';
import JSONAPISerializer from 'json-api-serializer';
import { storeToRefs } from 'pinia';

import apiConfig from '@/config';
import { Member } from '@/Member/member.entity';
import { useLoaderStore } from '@/stores/loader';
import { useMemberStore } from '@/stores/member';

declare module 'axios' {
    export interface AxiosRequestConfig {
        showLoader?: boolean;
    }
    export interface AxiosResponse<T = any, D = any> {
        data: T;
        status: number;
        statusText: string;
        headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
        config: InternalAxiosRequestConfig<D>;
        request?: any;
        meta?: any;
    }
}

class Http {
    private http: AxiosInstance;

    private deserializer: JSONAPISerializer;

    private member: Member | null = null;

    constructor(typeToDeserialize?: string) {
        const loaderStore = useLoaderStore();
        const memberStore = useMemberStore();
        this.member = storeToRefs(memberStore)?.member?.value;

        this.http = axios.create({
            baseURL: apiConfig.apiUrl,
            headers: {
                'Content-Type': 'application/json',
            },
            withCredentials: true,
            showLoader: true,
            timeout: 1000 * 60 * 10,
        });

        this.deserializer = new JSONAPISerializer();
        this.deserializer.register('members', {
            id: 'memberId',
            relationships: {
                litigations: {
                    type: 'litigations',
                },
            },
            afterDeserialize: (data: any) => {
                if (!Array.isArray(data.litigations)) {
                    data.litigations = [];
                }
                return data;
            },
        });

        this.deserializer.register('litigations', {
            id: 'litigationId',
            relationships: {
                criteria: {
                    type: 'criteria',
                },
                members: {
                    type: 'members',
                },
            },
        });

        this.deserializer.register('decisions', {
            id: 'decisionId',
        });

        this.deserializer.register('analyses', {
            id: 'analysisId',
            relationships: {
                entries: {
                    type: 'entries',
                },
                foremostEntries: {
                    type: 'entries',
                },
                errors: {
                    type: 'analysis_errors',
                },
                statuses: {
                    type: 'analysis_statuses',
                },
                decision: {
                    type: 'decisions',
                },
                litigation: {
                    type: 'litigations',
                },
                owner: {
                    type: 'members',
                },
            },
            afterDeserialize: (data: any) => {
                const arrayFields = ['entries', 'foremostEntries', 'errors', 'statuses'];
                arrayFields.forEach((field) => {
                    if (!Array.isArray(data[field])) {
                        data[field] = [];
                    }
                });
                return data;
            },
        });

        this.deserializer.register('analysis_errors', {
            id: 'errorId',
            relationships: {
                criterion: {
                    type: 'criteria',
                },
            },
        });

        this.deserializer.register('analysis_statuses', {
            id: 'statusId',
            relationships: {
                member: {
                    type: 'members',
                },
            },
        });

        this.deserializer.register('criteria', {
            id: 'criterionId',
            relationships: {
                entries: {
                    type: 'entries',
                },
            },
        });

        this.deserializer.register('entries', {
            id: 'entryId',
            relationships: {
                criteria: {
                    type: 'criteria',
                },
            },
        });

        this.deserializer.register('rules');

        this.http.interceptors.request.use(
            (config) => {
                if (config.showLoader) {
                    loaderStore.pending();
                }
                return config;
            },
            (error) => {
                if (error.config.showLoader) {
                    loaderStore.done();
                }
                return Promise.reject(error);
            },
        );

        this.http.interceptors.response.use(
            (response) => {
                if (response.config.showLoader) {
                    loaderStore.done();
                }

                if (!typeToDeserialize) {
                    return response;
                }

                const meta = Object.keys(response.data).includes('meta') ? { meta: response.data.meta } : null;

                return {
                    ...response,
                    ...meta,
                    ...{ data: this.deserializer.deserialize(typeToDeserialize, response.data) },
                };
            },
            (error) => {
                const { response } = error;

                if (response?.status === 401) {
                    memberStore.logOut();
                }

                if (response?.config?.showLoader) {
                    loaderStore.done();
                }

                return Promise.reject(error);
            },
        );
    }

    public async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response: AxiosResponse = await this.http.get(url, config);

        if (response.meta) {
            return { meta: response.meta, data: response.data } as any;
        }

        return response.data;
    }

    public async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
        const response = await this.http.get(url, { ...config, ...{ responseType: 'blob' } });

        return response.request.response;
    }

    public async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response: AxiosResponse = await this.http.post(url, data, config);
        return response.data;
    }

    public async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        const response: AxiosResponse = await this.http.put(url, data, config);
        return response.data;
    }

    public async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response: AxiosResponse = await this.http.delete(url, config);
        return response.data;
    }
}

export default Http;
